import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  Table, Button, Icon,
  Label, Popup, TextArea,
} from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, FileDropzone, ConfirmDialog, EvidenceTextButton } from "../../../Shared";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { AnnualPlan, Area, Activity, Frequency, User, Site, Company } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useSite, useCompany, useLanguage } from "../../../../contexts";
import { OHS_STATUSES } from "../../../../utils/constants";
import { computeAnnualPlanStatus, AP_STATUS_LABEL } from "../../../../utils/annualPlanStatus";
import { buildStandardDashboardPdf, drawPill, drawProgressCell, SEMANTIC_HEX } from "../../../Shared/pdfCharts";
import { isAdmin, isMaster } from "../../../../utils/checkPermission";
import { LegalRequirementDates } from "../../LegalRequirements/LegalRequirementDates";
import "./AnnualPlanTable.scss";

const AVATAR_COLORS = ["#1A5FA0", "#0E376E", "#3D6B9E", "#648EBC", "#4F7FB5", "#2A4F80", "#8AB3D4"];

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

function avatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

const PORTAL_INPUT = {
  width: "100%", padding: "10px 14px",
  border: "1px solid rgba(34,36,38,.15)", borderRadius: ".28571429rem",
  fontSize: "14px", outline: "none", background: "#fff",
  color: "rgba(0,0,0,.87)", boxSizing: "border-box", fontFamily: "inherit",
};
const PORTAL_BTN = {
  border: "none", borderRadius: "20px", padding: "10px 30px",
  fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer",
};

function AddCatalogPortal({ open, title, hasCode = false, onSave, onClose, t }) {
  const [code,   setCode]   = useState("");
  const [name,   setName]   = useState("");
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    if (open) { setCode(""); setName(""); setError(""); setSaving(false); }
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (hasCode && !code.trim()) { setError(t("code_required")); return; }
    if (!name.trim()) { setError(t("name_required")); return; }
    setSaving(true);
    try {
      const n = name.trim();
      const c = code.trim();
      await onSave({
        code: c ? c.charAt(0).toUpperCase() + c.slice(1).toLowerCase() : c,
        name: n.charAt(0).toUpperCase() + n.slice(1),
      });
      onClose();
    } catch (err) {
      setError(err?.msg || t("error_saving"));
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#F8F9FB", borderRadius: "20px", padding: "30px 32px 26px", width: "min(400px, 90vw)", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>
        <p style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1A2E4A", fontFamily: "'Archivo', sans-serif", margin: "0 0 20px" }}>{title}</p>
        {hasCode && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "6px" }}>{t("code")} <span style={{ color: "#e53935" }}>*</span></div>
            <input autoFocus value={code} onChange={e => { setCode(e.target.value); setError(""); }}
              placeholder="Ej: AR01" style={PORTAL_INPUT}
              onKeyDown={e => e.key === "Enter" && handleSave()} />
          </div>
        )}
        <div style={{ marginBottom: "0" }}>
          <div style={{ fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "6px" }}>{t("name")} <span style={{ color: "#e53935" }}>*</span></div>
          <input autoFocus={!hasCode} value={name} onChange={e => { setName(e.target.value); setError(""); }}
            placeholder="Ej: Higiene" style={PORTAL_INPUT}
            onKeyDown={e => e.key === "Enter" && handleSave()} />
        </div>
        {error && <p style={{ color: "#e53935", fontSize: "12px", margin: "6px 0 0" }}>{error}</p>}
        <div style={{ display: "flex", justifyContent: "center", gap: "14px", marginTop: "26px" }}>
          <button onClick={handleSave} disabled={saving}
            style={{ ...PORTAL_BTN, background: "#4A7FA5", color: "#fff", opacity: saving ? 0.7 : 1 }}>
            {saving ? "…" : t("save")}
          </button>
          <button onClick={onClose} style={{ ...PORTAL_BTN, background: "#9BAFC4", color: "#fff" }}>
            {t("cancel")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const apController        = new AnnualPlan();
const areaController      = new Area();
const activityController  = new Activity();
const frequencyController = new Frequency();
// const userController   = new User();
const siteCtrl            = new Site();
const companyCtrl         = new Company();

const STATUS_COLOR = Object.fromEntries(OHS_STATUSES.map(s => [s.value, s.color]));

const EMPTY = {
  area: "", actividades: "", responsable: "", frecuencia: "",
  fecha_programada: "", site: "", status: "", progress: 0, occurrences: [],
};

// ── Generación de ocurrencias (igual que LR) ──────────────────────────────
const FREQ_CONFIG = {
  "diario":        { addMonths: 0, addDays: 1 },
  "semanal":       { addMonths: 0, addDays: 7 },
  "quincenal":     { addMonths: 0, addDays: 15 },
  "mensual":       { addMonths: 1, addDays: 0 },
  "bimestral":     { addMonths: 2, addDays: 0 },
  "trimestral":    { addMonths: 3, addDays: 0 },
  "cuatrimestral": { addMonths: 4, addDays: 0 },
  "semestral":     { addMonths: 6, addDays: 0 },
  "anual":         { addMonths: 12, addDays: 0 },
};

function freqAddInterval(date, config) {
  const d = new Date(date);
  if (config.addMonths) d.setMonth(d.getMonth() + config.addMonths);
  else d.setDate(d.getDate() + config.addDays);
  return d;
}

function toDateStr(date) {
  if (!date) return "";
  const d = new Date(date);
  return isNaN(d) ? "" : d.toISOString().split("T")[0];
}

// Genera ocurrencias desde `startDateStr` en adelante, pero solo hasta el 31/12
// del año de esa fecha — nunca se extienden al año siguiente (ej: "Mensual"
// desde 08/08 da agosto..diciembre, 5 ocurrencias, no arrastra a enero).
function regenerateOccurrences(freqName, startDateStr, existingOccurrences) {
  const freqConfig = FREQ_CONFIG[freqName?.toLowerCase().trim()];
  if (!freqConfig || !startDateStr) return [];
  const existingByDate = {};
  (existingOccurrences || []).forEach((o) => { existingByDate[o.date] = o; });
  const result = [];
  let current = new Date(startDateStr + "T12:00:00");
  const yearEnd = new Date(current.getFullYear(), 11, 31, 12, 0, 0);
  let guard = 0;
  while (current <= yearEnd && guard < 366) {
    const ds = toDateStr(current);
    result.push(existingByDate[ds] || { date: ds, completed: false, comments: [], files: [] });
    current = freqAddInterval(current, freqConfig);
    guard++;
  }
  return result;
}
// ─────────────────────────────────────────────────────────────────────────────

function formatDate(date) {
  if (!date) return "-";
  const str = typeof date === "string" ? date : date.toISOString();
  const [y, m, d] = str.split("T")[0].split("-");
  return `${d}/${m}/${y}`;
}

const InlineSelect = ({ options, value, onChange, placeholder, addLabel, onAdd }) => (
  <select
    value={value || ""}
    onChange={(e) => {
      if (onAdd && e.target.value === "__add__") { onAdd(); }
      else onChange(e.target.value);
    }}
    className="ap-table__inline-select"
  >
    <option value="">{placeholder || "-"}</option>
    {options.map((o) => (
      <option key={o.key} value={o.value}>{o.text}</option>
    ))}
    {addLabel && <option value="__add__">{addLabel}</option>}
  </select>
);

const FilterDropdown = ({ options, value, onChange, placeholder, addLabel, onAdd }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="ap-table__fd" ref={ref}>
      <button type="button" className="ap-table__fd-toggle" onClick={() => setOpen((v) => !v)}>
        <span className={selected ? "" : "ap-table__fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        <span className="ap-table__fd-icons">
          {selected && (
            <span
              className="ap-table__fd-clear"
              onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
              title={placeholder}
            >
              ✕
            </span>
          )}
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`ap-table__fd-arrow${open ? " ap-table__fd-arrow--open" : ""}`}>
            <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="ap-table__fd-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`ap-table__fd-item${o.value === value ? " ap-table__fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
          {addLabel && (
            <button
              type="button"
              className="ap-table__fd-item ap-table__fd-item--add"
              onClick={() => { setOpen(false); onAdd && onAdd(); }}
            >
              {addLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export function AnnualPlanTable(props) {
  const { reload, onReload, extYear = null, role } = props;
  const { accessToken, user } = useAuth();
  const { site: siteFromContext } = useSite();
  const { company: companyFromContext } = useCompany();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const [searchParams, setSearchParams] = useSearchParams();

  // El Estado del Plan Anual se calcula (no se elige a mano): solo estos 4 valores.
  const STATUS_OPTIONS = Object.keys(AP_STATUS_LABEL).map((value) => ({
    key: value, value, text: AP_STATUS_LABEL[value],
  }));

  const canTransfer = isAdmin(role) || isMaster(role);
  const totalCols = canTransfer ? 9 : 8;
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [showTransfer,  setShowTransfer]  = useState(false);
  const [transferYear,  setTransferYear]  = useState((extYear || new Date().getFullYear()) + 1);
  const [transferring,  setTransferring]  = useState(false);
  const [openRowMenu, setOpenRowMenu] = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!openRowMenu) return;
    const handler = (e) => { if (!e.target.closest(".ap-table__row-menu, .ap-table__row-menu-list")) setOpenRowMenu(null); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openRowMenu]);

  const toggleRowMenu = (id, e) => {
    if (openRowMenu?.id === id) { setOpenRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 320;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setOpenRowMenu({
      id,
      left: rect.right - 190,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === itemsFilter.length && itemsFilter.length > 0) return new Set();
      return new Set(itemsFilter.map((i) => i._id));
    });
  };

  const openTransfer = () => {
    setTransferYear((extYear || new Date().getFullYear()) + 1);
    setShowTransfer(true);
  };

  const transferInFlightRef = useRef(false);
  const handleTransfer = async () => {
    if (transferInFlightRef.current) return;
    transferInFlightRef.current = true;
    setTransferring(true);
    try {
      const result = await apController.copyYear(accessToken, Array.from(selectedIds), transferYear);
      toast.success(`${t("ap_transfer_success")}: ${result.created} ${t("ap_transfer_created")}, ${result.skipped} ${t("ap_transfer_skipped")}`);
      setShowTransfer(false);
      setSelectedIds(new Set());
      onReload();
    } catch {
      toast.error(t("ap_transfer_error"));
    } finally {
      transferInFlightRef.current = false;
      setTransferring(false);
    }
  };

  const [items,        setItems]        = useState(null);
  const [itemsFilter,  setItemsFilter]  = useState([]);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [areaFilter,   setAreaFilter]   = useState("");
  const [siteFilter,      setSiteFilter]      = useState("");
  const [sites,           setSites]           = useState([]);
  const [companySiteIds,  setCompanySiteIds]  = useState([]);
  const [companyName,     setCompanyName]     = useState("");
  const noSiteInCtx    = !siteFromContext    || siteFromContext    === "0" || siteFromContext    === 0;
  const noCompanyInCtx = !companyFromContext || companyFromContext === "0" || companyFromContext === 0;

  const [catAreas,       setCatAreas]       = useState([]);
  const [catActivities,  setCatActivities]  = useState([]);
  const [catFrequencies, setCatFrequencies] = useState([]);
  // const [catUsers, setCatUsers] = useState([]);

  const [editingId,   setEditingId]   = useState(null);
  const [editingData, setEditingData] = useState(EMPTY);
  const [saving,      setSaving]      = useState(false);

  const [showAreaModal,      setShowAreaModal]      = useState(false);
  const [showActivityModal,  setShowActivityModal]  = useState(false);
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);

  // Modal states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDates,    setShowDates]    = useState(false);
  const [showObs,      setShowObs]      = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [showFiles,    setShowFiles]    = useState(false);
  const [showAttach,   setShowAttach]   = useState(false);
  const [showEscal,    setShowEscal]    = useState(false);
  const [escalItem,    setEscalItem]    = useState(null);
  const [escalForm,    setEscalForm]    = useState({});
  const [savingEscal,  setSavingEscal]  = useState(false);
  const [obsVal,         setObsVal]         = useState("");
  const [editingObsIdx,  setEditingObsIdx]  = useState(null);
  const [editingObsVal,  setEditingObsVal]  = useState("");
  const [editingObsDate, setEditingObsDate] = useState("");
  const [uploading,    setUploading]    = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [previewUrl,   setPreviewUrl]   = useState(null);
  const [previewName,  setPreviewName]  = useState("");

  const toOpts = (arr) => arr.map((x) => ({ key: x._id, value: x._id, text: x.name }));
  const toActivityOpts = (arr) => arr.map((x) => ({ key: x._id, value: x._id, text: x.name, area: x.area?._id || x.area || "" }));

  const reloadAreas = async (selectNew = false) => {
    try {
      const prevIds = catAreas.map((a) => a.key);
      const opts = toOpts(await areaController.getAreas(accessToken));
      setCatAreas(opts);

      if (selectNew) {
        const added = opts.find((o) => !prevIds.includes(o.key));
        if (added) setField("area", added.value);
      }
    } catch {}
  };

  const reloadActivities = async (selectNew = false) => {
    try {
      const prevIds = catActivities.map((a) => a.key);
      const opts = toActivityOpts(await activityController.getActivities(accessToken));
      setCatActivities(opts);
      if (selectNew) {
        const added = opts.find((o) => !prevIds.includes(o.key));
        if (added) setField("actividades", added.value);
      }
    } catch {}
  };

  const reloadFrequencies = async (selectNew = false) => {
    try {
      const prevIds = catFrequencies.map((f) => f.key);
      const opts = toOpts(await frequencyController.getFrequencies(accessToken));
      setCatFrequencies(opts);
      if (selectNew) {
        const added = opts.find((o) => !prevIds.includes(o.key));
        if (added) setField("frecuencia", added.value);
      }
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const [a, ac, fr] = await Promise.allSettled([
          areaController.getAreas(accessToken),
          activityController.getActivities(accessToken),
          frequencyController.getFrequencies(accessToken),
        ]);
        const areas = a.status === "fulfilled" ? toOpts(a.value) : [];
        setCatAreas(areas);
        setCatActivities(ac.status  === "fulfilled" ? toActivityOpts(ac.value) : []);
        setCatFrequencies(fr.status === "fulfilled" ? toOpts(fr.value) : []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const filters = {};
        if (areaFilter) filters.area = areaFilter;
        const res = await apController.getAnnualPlans(accessToken, filters);
        setItems(res);
        setItemsFilter(res);
      } catch {
        setItems([]);
        setItemsFilter([]);
      }
    })();
  }, [reload, areaFilter]);

  // Abrir un registro puntual al llegar con ?open=<id> (ej. desde el buscador
  // de evidencias, "Ver documento").
  useEffect(() => {
    if (!items) return;
    const openId = searchParams.get("open");
    if (!openId) return;
    const target = items.find((i) => i._id === openId);
    if (target) startEdit(target);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [items, searchParams]);

  // useEffect(() => {
  //   if (!siteFromContext) return;
  //   userController.getUsersBySite(accessToken, siteFromContext, true)
  //     .then((res) => {
  //       const list = Array.isArray(res) ? res : [];
  //       setCatUsers(list.map((u) => ({
  //         key:   u._id,
  //         value: `${u.firstname || ""} ${u.lastname || ""}`.trim(),
  //         text:  `${u.firstname || ""} ${u.lastname || ""}`.trim(),
  //       })));
  //     })
  //     .catch(() => setCatUsers([]));
  // }, [siteFromContext, accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    const load = !noCompanyInCtx
      ? siteCtrl.getSitesByCompany(accessToken, companyFromContext, true)
      : siteCtrl.getSites(accessToken, true);
    load.then(res => setSites(Array.isArray(res) ? res : [])).catch(() => {});
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  useEffect(() => { setSiteFilter(""); }, [siteFromContext]);

  useEffect(() => {
    if (noCompanyInCtx) { setCompanySiteIds([]); return; }
    siteCtrl.getSitesByCompany(accessToken, companyFromContext, true)
      .then(res => setCompanySiteIds((res || []).map(s => s._id)))
      .catch(() => setCompanySiteIds([]));
  }, [companyFromContext, accessToken]);

  useEffect(() => {
    if (noCompanyInCtx) { setCompanyName(""); return; }
    companyCtrl.getCompany(accessToken, companyFromContext)
      .then(res => setCompanyName(res?.name || ""))
      .catch(() => setCompanyName(""));
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  useEffect(() => {
    if (!items) return;
    let filtered = [...items];
    if (extYear) {
      filtered = filtered.filter((item) => {
        if (item.year !== undefined && item.year !== null) return item.year === extYear;
        if (item.fecha_programada) return new Date(item.fecha_programada).getFullYear() === extYear;
        if (item.occurrences?.length)
          return item.occurrences.some((o) => new Date(o.date).getFullYear() === extYear);
        return new Date(item.createdAt).getFullYear() === extYear;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter((item) =>
        item.area?.name?.toLowerCase().includes(q) ||
        item.actividades?.name?.toLowerCase().includes(q) ||
        item.status?.toLowerCase().includes(q) ||
        AP_STATUS_LABEL[item.status]?.toLowerCase().includes(q)
      );
    }
    if (statusFilter) filtered = filtered.filter((item) => item.status === statusFilter);
    if (!noSiteInCtx) {
      filtered = filtered.filter((item) => (item.site?._id || item.site) === siteFromContext);
    } else if (!noCompanyInCtx && companySiteIds.length > 0) {
      filtered = filtered.filter((item) => companySiteIds.includes(item.site?._id || item.site));
      if (siteFilter) filtered = filtered.filter((item) => (item.site?._id || item.site) === siteFilter);
    } else if (siteFilter) {
      filtered = filtered.filter((item) => (item.site?._id || item.site) === siteFilter);
    }
    setItemsFilter(filtered);
  }, [search, statusFilter, extYear, siteFilter, siteFromContext, noSiteInCtx, noCompanyInCtx, companySiteIds, items]);

  useEffect(() => { setSelectedIds(new Set()); }, [extYear, reload]);

  const handleExportXLSX = () => {
    if (!itemsFilter.length) return;
    const rows = itemsFilter.map((item, i) => ({
      "N°":            i + 1,
      "Área":          item.area?.name || "-",
      "Actividad":     item.actividades?.name || "-",
      "Responsable":   item.responsable || "-",
      "Frecuencia":    item.frecuencia?.name || "-",
      "Fecha programada": item.fecha_programada ? item.fecha_programada.split("T")[0] : "-",
      "Estado":        AP_STATUS_LABEL[item.status] || "-",
      "Progreso (%)":  item.progress ?? 0,
      "Sitio":         item.site?.name || "-",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plan Anual");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "plan_anual.xlsx");
  };

  const apExportRows = () => itemsFilter.map((item, i) => ({
    "N°":            i + 1,
    "Área":          item.area?.name || "-",
    "Actividad":     item.actividades?.name || "-",
    "Responsable":   item.responsable || "-",
    "Frecuencia":    item.frecuencia?.name || "-",
    "Fecha programada": item.fecha_programada ? item.fecha_programada.split("T")[0] : "-",
    "Estado":        AP_STATUS_LABEL[item.status] || "-",
    "Progreso (%)":  item.progress ?? 0,
    "Sitio":         item.site?.name || "-",
  }));

  const handleExportCSV = () => {
    const data = apExportRows();
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csv = [headers.join(","), ...data.map((r) => headers.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(","))].join("\n");
    saveAs(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }), "plan_anual.csv");
  };

  const handleExportPDF = async () => {
    if (!itemsFilter.length) return;
    const siteName = !noSiteInCtx
      ? sites.find(s => s._id === siteFromContext)?.name
      : siteFilter ? sites.find(s => s._id === siteFilter)?.name : null;

    const tableColumns = [
      // Con "Total Grupo" (sin sitio puntual elegido) los datos vienen de
      // varios sitios mezclados — sin esta columna no se puede saber a
      // cuál pertenece cada fila (igual que en el export PDF del dashboard).
      ...(noSiteInCtx ? [{ key: "site", header: t("site"), flex: 1, value: (r) => r.site?.name || "-" }] : []),
      { key: "area", header: t("area"), flex: 1.2, value: (r) => r.area?.name || "-" },
      { key: "activity", header: t("activity"), flex: 1.4, value: (r) => r.actividades?.name || "-" },
      { key: "responsible", header: t("responsible"), flex: 1, value: (r) => r.responsable || "-" },
      { key: "frequency", header: t("frequency"), flex: 1, value: (r) => r.frecuencia?.name || "-" },
      {
        // jsPDF con fuentes estándar (Helvetica) sólo soporta WinAnsi/CP1252: la
        // flecha "→" no está en esa tabla y corrompe la celda al dibujarla acá.
        key: "scheduledDate", header: t("scheduled_date"), flex: 1.6,
        value: (r) => (r.occurrences?.length > 1
          ? `${formatDate(r.occurrences[0].date)} - ${formatDate(r.occurrences[r.occurrences.length - 1].date)}`
          : formatDate(r.occurrences?.[0]?.date || r.fecha_programada)),
      },
      {
        key: "status", header: t("status"), flex: 1, align: "center",
        render: (doc, { x, y, width, height, row }) => {
          if (!row.status) return;
          drawPill(doc, { x: x + width / 2, y: y + height / 2, text: AP_STATUS_LABEL[row.status] || row.status, color: SEMANTIC_HEX[STATUS_COLOR[row.status]] || SEMANTIC_HEX.grey });
        },
      },
      {
        key: "progress", header: t("progress"), flex: 1.2,
        render: (doc, { x, y, width, height, row }) => {
          drawProgressCell(doc, { x, y, width, height, pct: row.progress || 0 });
        },
      },
    ];

    await buildStandardDashboardPdf({
      jsPDFCtor: jsPDF,
      pageTitle: t("annual_plan") || "Plan Anual",
      companyName: [companyName, siteName].filter(Boolean).join(" · "),
      generatedOnLabel: t("generated_on"),
      table: { columns: tableColumns, rows: itemsFilter, rowHeight: 24, headerHeight: 22, emptyMessage: t("no_results") },
      tableTitle: t("annual_plan") || "Plan Anual",
      filename: "plan_anual.pdf",
    });
  };

  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target))
        setShowExportMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showExportMenu]);

  const getId = (val) => (val && typeof val === "object" ? val._id : val) || "";

  const startNew = () => {
    setEditingId("new");
    setEditingData({ ...EMPTY, site: siteFromContext || "" });
  };

  const startEdit = (item) => {
    setEditingId(item._id);
    setEditingData({
      area:             getId(item.area),
      actividades:      getId(item.actividades),
      responsable:      item.responsable || "",
      frecuencia:       getId(item.frecuencia),
      fecha_programada: item.fecha_programada ? item.fecha_programada.split("T")[0] : "",
      site:             getId(item.site),
      status:           item.status    || "",
      progress:         item.progress  ?? 0,
      occurrences:      item.occurrences || [],
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditingData(EMPTY); };

  const setField = (field, value) => setEditingData((prev) => ({ ...prev, [field]: value }));

  const handleSaveEdit = async () => {
    if (!editingData.area || !editingData.site) {
      toast.warning(t("ap_area_site_required"));
      return;
    }
    setSaving(true);
    try {
      const refFields = ["area", "actividades", "frecuencia", "site", "status", "fecha_programada", "responsable"];
      const payload = { ...editingData };
      refFields.forEach((f) => { if (!payload[f]) delete payload[f]; });

      if (editingId === "new") {
        await apController.createAnnualPlan(accessToken, payload);
        toast.success(t("ap_created"));
      } else {
        await apController.updateAnnualPlan(accessToken, editingId, payload);
        toast.success(t("ap_updated"));
      }
      cancelEdit();
      onReload();
    } catch (err) {
      toast.error(err?.msg || t("ap_error_save"));
    } finally {
      setSaving(false);
    }
  };

  const isImage = (name) => /\.(jpg|jpeg|png|gif|webp)$/i.test(name || "");

  const openProgress = (item) => { setSelectedItem(item); setShowDates(true); };
  const openObs      = (item) => { setSelectedItem(item); setObsVal(""); setShowObs(true); };
  const openDelete   = (item) => { setSelectedItem(item); setShowConfirm(true); };
  const openAttach   = (item) => { setSelectedItem(item); setShowAttach(true); };
  const openFiles    = (item) => { setSelectedItem(item); setShowFiles(true); };

  const openEscal = (item) => {
    setEscalItem(item);
    setEscalForm({
      resp1:       item.resp1       || "",
      resp1_email: item.resp1_email || "",
      resp2:       item.resp2       || "",
      resp2_email: item.resp2_email || "",
      resp3:       item.resp3       || "",
      resp3_email: item.resp3_email || "",
      rule_l1:     item.rule_l1     ?? 0,
      rule_l2:     item.rule_l2     ?? 3,
      rule_l3:     item.rule_l3     ?? 7,
    });
    setShowEscal(true);
  };

  const handleSaveEscal = async () => {
    if (!escalItem) return;
    setSavingEscal(true);
    try {
      await apController.updateAnnualPlan(accessToken, escalItem._id, escalForm);
      setItems((prev) => prev.map((it) => it._id === escalItem._id ? { ...it, ...escalForm } : it));
      setShowEscal(false);
      toast.success(t("ap_escalation_saved"));
    } catch {
      toast.error(t("ap_escalation_error"));
    } finally {
      setSavingEscal(false);
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await apController.uploadFile(accessToken, selectedItem._id, formData);
      }
      toast.success(t("files_uploaded"));
      setShowAttach(false);
      onReload();
    } catch { toast.error(t("error_uploading_files")); }
    finally { setUploading(false); }
  };

  const handleDeleteFile = async (fileKey) => {
    setDeletingFile(fileKey);
    try {
      await apController.deleteFile(accessToken, selectedItem._id, fileKey);
      toast.success(t("file_deleted"));
      setSelectedItem((prev) => ({ ...prev, files: prev.files.filter((f) => f.key !== fileKey) }));
      onReload();
    } catch { toast.error(t("error_downloading_file")); }
    finally { setDeletingFile(null); }
  };

  const handleViewFile = async (fileKey, fileName) => {
    try {
      const blobUrl = await apController.getFile(accessToken, fileKey);
      if (!blobUrl) { toast.error(t("error_downloading_file")); return; }
      if (isImage(fileName)) {
        setPreviewUrl(blobUrl);
        setPreviewName(fileName);
      } else {
        window.open(blobUrl, "_blank");
      }
    } catch { toast.error(t("error_downloading_file")); }
  };

  const handleDelete = async () => {
    try {
      await apController.deleteAnnualPlan(accessToken, selectedItem._id);
      toast.success(t("ap_deleted"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("ap_error_delete")); }
  };

  const handleSaveDates = async (occs, prog) => {
    try {
      await apController.updateAnnualPlan(accessToken, selectedItem._id, { occurrences: occs, progress: prog });
      toast.success(t("ap_saved_progress"));
      setShowDates(false);
      onReload();
    } catch { toast.error(t("ap_error_progress")); }
  };

  const userName = user ? `${user.firstname || ""} ${user.lastname || ""}`.trim() : "Usuario";

  const handleSaveObs = async () => {
    if (!obsVal.trim()) return;
    setSaving(true);
    try {
      const newObs = { text: obsVal.trim(), userName, createdAt: new Date() };
      const updatedObs = [...(selectedItem.observations || []), newObs];
      await apController.updateAnnualPlan(accessToken, selectedItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setSelectedItem((prev) => ({ ...prev, observations: updatedObs }));
      setObsVal("");
      onReload();
    } catch { toast.error(t("error_saving_observations")); }
    finally { setSaving(false); }
  };

  const handleSaveObsEdit = async () => {
    if (!editingObsVal.trim()) return;
    setSaving(true);
    try {
      const updatedObs = (selectedItem.observations || []).map((o, i) =>
        i === editingObsIdx
          ? { ...o, text: editingObsVal.trim(), createdAt: editingObsDate ? new Date(editingObsDate) : o.createdAt }
          : o
      );
      await apController.updateAnnualPlan(accessToken, selectedItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setSelectedItem((prev) => ({ ...prev, observations: updatedObs }));
      setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate("");
    } catch { toast.error(t("error_saving_observations")); }
    finally { setSaving(false); }
  };

  const handleDeleteObs = async (origIdx) => {
    try {
      const updatedObs = (selectedItem.observations || []).filter((_, i) => i !== origIdx);
      await apController.updateAnnualPlan(accessToken, selectedItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setSelectedItem((prev) => ({ ...prev, observations: updatedObs }));
      onReload();
    } catch { toast.error(t("error_saving_observations")); }
  };

  const renderEditRow = () => (
    <Table.Row key={editingId} positive={editingId === "new"} warning={editingId !== "new"}>
      {canTransfer && <Table.Cell />}
      {/* Área */}
      <Table.Cell>
        <select
          value={editingData.area || ""}
          onChange={(e) => {
            if (e.target.value === "__add_area__") { setShowAreaModal(true); return; }
            setEditingData((prev) => ({
              ...prev,
              area: e.target.value,
              actividades: catActivities.find((a) => a.value === prev.actividades)?.area === e.target.value
                ? prev.actividades
                : "",
            }));
          }}
          className="ap-table__inline-select"
        >
          <option value="">{t("area")} *</option>
          {catAreas.map((o) => <option key={o.key} value={o.value}>{o.text}</option>)}
          <option value="__add_area__">{"+ " + t("new_area")}</option>
        </select>
      </Table.Cell>

      {/* Actividades */}
      <Table.Cell>
        <InlineSelect
          options={editingData.area ? catActivities.filter((a) => a.area === editingData.area) : catActivities}
          value={editingData.actividades}
          onChange={(v) => setField("actividades", v)}
          placeholder={t("activity")}
          addLabel={"+ " + t("new_activity")}
          onAdd={() => setShowActivityModal(true)}
        />
      </Table.Cell>

      {/* Responsable */}
      <Table.Cell>
        <input
          type="text"
          value={editingData.responsable}
          onChange={(e) => setField("responsable", e.target.value)}
          placeholder={t("responsible")}
          className="ap-table__inline-input"
        />
        {/* Para seleccionar desde lista de usuarios del sitio, descomentar:
        <InlineSelect
          options={catUsers}
          value={editingData.responsable}
          onChange={(v) => setField("responsable", v)}
          placeholder={t("responsible")}
        /> */}
      </Table.Cell>

      {/* Frecuencia */}
      <Table.Cell>
        <InlineSelect
          options={catFrequencies}
          value={editingData.frecuencia}
          placeholder={t("frequency")}
          addLabel={"+ " + t("new_frequency")}
          onChange={(v) => {
            const freqName = catFrequencies.find((f) => f.value === v)?.text || "";
            const startDateStr = editingData.occurrences?.[0]?.date || editingData.fecha_programada;
            const newOccs = regenerateOccurrences(freqName, startDateStr, editingData.occurrences);
            setEditingData((prev) => ({
              ...prev,
              frecuencia: v,
              occurrences: newOccs.length ? newOccs : prev.occurrences,
            }));
          }}
          onAdd={() => setShowFrequencyModal(true)}
        />
      </Table.Cell>

      {/* Fecha programada */}
      <Table.Cell>
        {(() => {
          const freqName = catFrequencies.find((f) => f.value === editingData.frecuencia)?.text?.toLowerCase().trim() || "";
          const isRecurring = Boolean(FREQ_CONFIG[freqName]);
          if (isRecurring && editingData.occurrences?.length > 1) {
            return (
              <span style={{ fontSize: "0.82em", color: "#555", whiteSpace: "nowrap" }}>
                {formatDate(editingData.occurrences[0].date)} → {formatDate(editingData.occurrences[editingData.occurrences.length - 1].date)}
              </span>
            );
          }
          if (isRecurring && editingData.occurrences?.length === 1) {
            return (
              <span style={{ fontSize: "0.85em", color: "#555" }}>
                {formatDate(editingData.occurrences[0].date)}
              </span>
            );
          }
          return (
            <input
              type="date"
              value={editingData.fecha_programada}
              onChange={(e) => {
                const newDate = e.target.value;
                const newOccs = regenerateOccurrences(freqName, newDate, editingData.occurrences);
                setEditingData((prev) => ({
                  ...prev,
                  fecha_programada: newDate,
                  occurrences: newOccs.length
                    ? newOccs
                    : prev.occurrences?.length > 0
                      ? [{ ...prev.occurrences[0], date: newDate }]
                      : [],
                }));
              }}
              style={{ width: "100%" }}
            />
          );
        })()}
      </Table.Cell>

      {/* Estado — se calcula solo a partir del avance y la fecha programada */}
      <Table.Cell textAlign="center">
        {(() => {
          const liveStatus = computeAnnualPlanStatus(editingData.progress, editingData.fecha_programada);
          return (
            <Label size="small" color={STATUS_COLOR[liveStatus] || "grey"}>
              {AP_STATUS_LABEL[liveStatus]}
            </Label>
          );
        })()}
      </Table.Cell>

      {/* Avance — solo lectura, se calcula al tildar fechas */}
      <Table.Cell textAlign="center">
        <span style={{ fontSize: "0.88em", fontWeight: 600, color: "#555" }}>{editingData.progress || 0}%</span>
      </Table.Cell>

      {/* Guardar / Cancelar */}
      <Table.Cell textAlign="center" className="ap-table__actions">
        <Popup size="mini" content={t("save")} trigger={
          <Button icon size="mini" primary loading={saving} disabled={saving} onClick={handleSaveEdit}>
            <Icon name="save" />
          </Button>
        } />
        <Popup size="mini" content={t("cancel")} trigger={
          <Button icon size="mini" onClick={cancelEdit} disabled={saving}>
            <Icon name="close" />
          </Button>
        } />
      </Table.Cell>
    </Table.Row>
  );

  const renderQuickBar = () => (
    <div className="ap-table__quick-bar">
      <span className="ap-table__quick-bar-label">{t("ap_new_plan_label")}</span>
      <div className="ap-table__quick-fields">
        {/* Área */}
        <div className="ap-table__quick-field">
          <label>{t("area")} *</label>
          <FilterDropdown
            options={catAreas}
            value={editingData.area || ""}
            onChange={(v) => {
              setEditingData((prev) => ({
                ...prev,
                area: v,
                actividades: catActivities.find((a) => a.value === prev.actividades)?.area === v
                  ? prev.actividades
                  : "",
              }));
            }}
            placeholder={t("area")}
            addLabel={"+ " + t("new_area")}
            onAdd={() => setShowAreaModal(true)}
          />
        </div>

        {/* Actividad */}
        <div className="ap-table__quick-field">
          <label>{t("activity")}</label>
          <FilterDropdown
            options={editingData.area ? catActivities.filter((a) => a.area === editingData.area) : catActivities}
            value={editingData.actividades}
            onChange={(v) => setField("actividades", v)}
            placeholder={t("activity")}
            addLabel={"+ " + t("new_activity")}
            onAdd={() => setShowActivityModal(true)}
          />
        </div>

        {/* Responsable */}
        <div className="ap-table__quick-field">
          <label>{t("responsible")}</label>
          <input
            type="text"
            value={editingData.responsable}
            onChange={(e) => setField("responsable", e.target.value)}
            placeholder={t("responsible")}
            className="ap-table__quick-input"
          />
        </div>

        {/* Frecuencia */}
        <div className="ap-table__quick-field">
          <label>{t("frequency")}</label>
          <FilterDropdown
            options={catFrequencies}
            value={editingData.frecuencia}
            placeholder={t("frequency")}
            addLabel={"+ " + t("new_frequency")}
            onChange={(v) => {
              const freqName = catFrequencies.find((f) => f.value === v)?.text || "";
              const startDateStr = editingData.occurrences?.[0]?.date || editingData.fecha_programada;
              const newOccs = regenerateOccurrences(freqName, startDateStr, editingData.occurrences);
              setEditingData((prev) => ({
                ...prev,
                frecuencia: v,
                occurrences: newOccs.length ? newOccs : prev.occurrences,
              }));
            }}
            onAdd={() => setShowFrequencyModal(true)}
          />
        </div>

        {/* Fecha programada */}
        <div className="ap-table__quick-field">
          <label>{t("scheduled_date")}</label>
          {(() => {
            const freqName = catFrequencies.find((f) => f.value === editingData.frecuencia)?.text?.toLowerCase().trim() || "";
            const isRecurring = Boolean(FREQ_CONFIG[freqName]);
            if (isRecurring && editingData.occurrences?.length > 1) {
              return (
                <span style={{ fontSize: "0.82em", color: "#555", whiteSpace: "nowrap" }}>
                  {formatDate(editingData.occurrences[0].date)} → {formatDate(editingData.occurrences[editingData.occurrences.length - 1].date)}
                </span>
              );
            }
            if (isRecurring && editingData.occurrences?.length === 1) {
              return (
                <span style={{ fontSize: "0.85em", color: "#555" }}>
                  {formatDate(editingData.occurrences[0].date)}
                </span>
              );
            }
            return (
              <input
                type="date"
                value={editingData.fecha_programada}
                onChange={(e) => {
                  const newDate = e.target.value;
                  const newOccs = regenerateOccurrences(freqName, newDate, editingData.occurrences);
                  setEditingData((prev) => ({
                    ...prev,
                    fecha_programada: newDate,
                    occurrences: newOccs.length
                      ? newOccs
                      : prev.occurrences?.length > 0
                        ? [{ ...prev.occurrences[0], date: newDate }]
                        : [],
                  }));
                }}
                className="ap-table__quick-input"
              />
            );
          })()}
        </div>

        {/* Estado — se calcula solo, no se elige */}
        <div className="ap-table__quick-field">
          <label>{t("status")}</label>
          <div className="ap-table__quick-status">
            {(() => {
              const liveStatus = computeAnnualPlanStatus(editingData.progress, editingData.fecha_programada);
              return (
                <Label size="small" color={STATUS_COLOR[liveStatus] || "grey"}>
                  {AP_STATUS_LABEL[liveStatus]}
                </Label>
              );
            })()}
          </div>
        </div>
      </div>

      <div className="ap-table__quick-actions">
        <Popup size="mini" content={t("save")} trigger={
          <Button icon size="mini" primary loading={saving} disabled={saving} onClick={handleSaveEdit} className="ap-table__save-btn">
            <Icon name="save" />
          </Button>
        } />
        <Popup size="mini" content={t("cancel")} trigger={
          <Button icon size="mini" onClick={cancelEdit} disabled={saving}>
            <Icon name="close" />
          </Button>
        } />
      </div>
    </div>
  );

  const renderDatesRow = (item) => (
    <Table.Row className="ap-table__dates-row">
      <Table.Cell colSpan={totalCols}>
        <LegalRequirementDates
          evidenceModule="annualPlan"
          key={editingData.frecuencia}
          hideTitle
          capOccurrencesToYear
          item={{
            frequency: catFrequencies.find((f) => f.value === editingData.frecuencia)
              ? { name: catFrequencies.find((f) => f.value === editingData.frecuencia).text }
              : (item?.frecuencia || null),
            occurrences: editingData.occurrences?.length
              ? editingData.occurrences
              : (item?.occurrences || []),
            expiration: editingData.fecha_programada || item?.fecha_programada,
          }}
          close={cancelEdit}
          onReload={onReload}
          onLocalSave={(occs, prog) => {
            setField("occurrences", occs);
            setField("progress", prog);
          }}
        />
      </Table.Cell>
    </Table.Row>
  );

  return (
    <div className="ap-table">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Acciones */}
      <div className="ap-table__action-row">
          <div className="ap-table__export-dropdown" ref={exportMenuRef}>
            <button className="ap-table__export-toggle" onClick={() => setShowExportMenu(v => !v)}>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="13" height="13">
                <path d="M3 13h10M8 3v7m0 0L5 7m3 3 3-3"/>
              </svg>
              {t("ap_export")}
              <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9"><path d="M6 8L2 4h8z"/></svg>
            </button>
            {showExportMenu && (
              <div className="ap-table__export-menu">
                <button className="ap-table__export-menu-item" onClick={() => { handleExportCSV(); setShowExportMenu(false); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  CSV
                </button>
                <button className="ap-table__export-menu-item" onClick={() => { handleExportXLSX(); setShowExportMenu(false); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Excel (XLSX)
                </button>
                <button className="ap-table__export-menu-item" onClick={() => { handleExportPDF(); setShowExportMenu(false); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  PDF
                </button>
              </div>
            )}
          </div>
          {canTransfer && (
            <button
              className="ap-table__export-toggle"
              onClick={openTransfer}
              disabled={selectedIds.size === 0}
              title={selectedIds.size === 0 ? t("ap_transfer_none_selected") : undefined}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="13" height="13">
                <path d="M5 4h7l-2-2m2 2-2 2M11 12H4l2 2m-2-2 2-2"/>
              </svg>
              {t("ap_transfer_button")}{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
            </button>
          )}
          {!noSiteInCtx && (
            <button className="ap-table__new-btn" onClick={startNew} title={t("ap_new_plan")}>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                <path d="M8 3v10M3 8h10"/>
              </svg>
            </button>
          )}
      </div>

      {/* Filtros + tabla, en una sola card */}
      <div className="ap-table__card">
        <div className="ap-table__filters">
          <div className="ap-table__filter-block ap-table__filter-search">
            <div className="ap-table__search-wrap">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="ap-table__filter-input"
                placeholder={t("ap_search_ph")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          {noSiteInCtx && sites.length > 0 && (
            <div className="ap-table__filter-block">
              <FilterDropdown
                options={sites.map((s) => ({ key: s._id, value: s._id, text: s.name }))}
                value={siteFilter}
                onChange={setSiteFilter}
                placeholder={t("site")}
              />
            </div>
          )}
          <div className="ap-table__filter-block">
            <FilterDropdown
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder={t("status")}
            />
          </div>
          <div className="ap-table__filter-block">
            <FilterDropdown
              options={catAreas}
              value={areaFilter}
              onChange={setAreaFilter}
              placeholder={t("area")}
            />
          </div>
        </div>

        {/* Barra rápida de creación — visible arriba de la tabla al cargar una nueva actividad */}
        {editingId === "new" && renderQuickBar()}

        {/* Tabla */}
        {items === null ? (
          <LoadingSpinner />
        ) : itemsFilter.length === 0 && editingId !== "new" ? (
          <div className="ap-table__empty">{t("no_results")}</div>
        ) : (
          <div className="ap-table__tbl-scroll">
          <Table className="ap-table__tbl unstackable rtable-cards">
            <Table.Header>
              <Table.Row>
                {canTransfer && (
                  <Table.HeaderCell textAlign="center">
                    <input
                      type="checkbox"
                      checked={itemsFilter.length > 0 && selectedIds.size === itemsFilter.length}
                      onChange={toggleSelectAll}
                    />
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell>{t("area")}</Table.HeaderCell>
                <Table.HeaderCell>{t("activity")}</Table.HeaderCell>
                <Table.HeaderCell>{t("responsible")}</Table.HeaderCell>
                <Table.HeaderCell>{t("frequency")}</Table.HeaderCell>
                <Table.HeaderCell>{t("scheduled_date")}</Table.HeaderCell>
                <Table.HeaderCell>{t("status")}</Table.HeaderCell>
                <Table.HeaderCell>{t("progress")}</Table.HeaderCell>
                <Table.HeaderCell textAlign="center"></Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {itemsFilter.map((item) =>
                  editingId === item._id ? (
                    <React.Fragment key={item._id}>
                      {renderEditRow()}
                      {renderDatesRow(item)}
                    </React.Fragment>
                  ) : (
                    <Table.Row key={item._id}>
                      {canTransfer && (
                        <Table.Cell textAlign="center" data-label="">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(item._id)}
                            onChange={() => toggleSelected(item._id)}
                          />
                        </Table.Cell>
                      )}
                      <Table.Cell data-label={t("area")}>{item.area?.name        || "-"}</Table.Cell>
                      <Table.Cell data-label={t("activity")}>{item.actividades?.name || "-"}</Table.Cell>
                      <Table.Cell data-label={t("responsible")}>{item.responsable       || "-"}</Table.Cell>
                      <Table.Cell data-label={t("frequency")}>{item.frecuencia?.name  || "-"}</Table.Cell>
                      <Table.Cell data-label={t("scheduled_date")}>
                        {item.occurrences?.length > 1 ? (
                          <span style={{ fontSize: "0.82em", color: "#555", whiteSpace: "nowrap" }}>
                            {formatDate(item.occurrences[0].date)} → {formatDate(item.occurrences[item.occurrences.length - 1].date)}
                          </span>
                        ) : (
                          <span>{formatDate(item.occurrences?.[0]?.date || item.fecha_programada)}</span>
                        )}
                      </Table.Cell>
                      <Table.Cell data-label={t("status")}>
                        {item.status
                          ? <Label size="small" color={STATUS_COLOR[item.status] || "grey"}>
                              {STATUS_OPTIONS.find(o => o.value === item.status)?.text || item.status}
                            </Label>
                          : "-"}
                      </Table.Cell>
                      <Table.Cell data-label={t("progress")}>
                        <span style={{ fontSize: "0.85em", fontWeight: 600 }}>{item.progress || 0}%</span>
                      </Table.Cell>

                      {/* Acciones */}
                      <Table.Cell textAlign="center" className="ap-table__actions" onClick={(e) => e.stopPropagation()}>
                        <div className="ap-table__row-menu">
                          <button
                            type="button"
                            className="ap-table__row-menu-btn"
                            onClick={(e) => toggleRowMenu(item._id, e)}
                          >
                            <Icon name="ellipsis vertical" />
                          </button>
                        </div>
                        {openRowMenu?.id === item._id && ReactDOM.createPortal(
                          <div
                            className="ap-table__row-menu-list"
                            style={{
                              position: "fixed",
                              top: openRowMenu.top,
                              left: openRowMenu.left,
                              transform: openRowMenu.openUp ? "translateY(-100%)" : "none",
                            }}
                          >
                            <button onClick={() => { startEdit(item); setOpenRowMenu(null); }} disabled={editingId !== null}>
                              <Icon name="pencil" />{t("edit")}
                            </button>
                            <button onClick={() => { openProgress(item); setOpenRowMenu(null); }}>
                              <Icon name="chart line" />{t("register_progress")}
                            </button>
                            <button onClick={() => { openObs(item); setOpenRowMenu(null); }}>
                              <Icon name="comment outline" />{t("observations")}
                            </button>
                            <button onClick={() => { openAttach(item); setOpenRowMenu(null); }}>
                              <Icon name="attach" />{t("attach_files")}
                            </button>
                            <button onClick={() => { openEscal(item); setOpenRowMenu(null); }}>
                              <Icon name="bell outline" />{t("ap_escalation_config")}
                            </button>
                            <button onClick={() => { openFiles(item); setOpenRowMenu(null); }}>
                              <Icon name="folder open outline" />{t("view_files")}
                            </button>
                            <button className="ap-table__row-menu-item--danger" onClick={() => { openDelete(item); setOpenRowMenu(null); }} disabled={editingId !== null}>
                              <Icon name="trash" />{t("delete")}
                            </button>
                          </div>,
                          document.body
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )
                )
              }
            </Table.Body>
          </Table>
          </div>
        )}
      </div>

      {/* Modal Avance — fechas y ocurrencias */}
      {showDates && selectedItem && ReactDOM.createPortal(
        <div className="apt-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowDates(false); }}>
          <div className="apt-obs-modal apt-obs-modal--wide">
            <div className="apt-obs-header">
              <p className="apt-obs-title">{t("register_progress")}</p>
              <button className="apt-obs-close" onClick={() => setShowDates(false)}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="apt-obs-body">
              <LegalRequirementDates
                evidenceModule="annualPlan"
                item={{
                  _id: selectedItem._id,
                  frequency: { name: selectedItem.frecuencia?.name || "" },
                  occurrences: selectedItem.occurrences || [],
                  expiration: selectedItem.fecha_programada,
                }}
                close={() => setShowDates(false)}
                onReload={onReload}
                customSave={handleSaveDates}
                fileApi={apController}
                showFooter
                hideTitle
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Observaciones */}
      {showObs && ReactDOM.createPortal(
        <div className="apt-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); } }}>
          <div className="apt-obs-modal">
            <div className="apt-obs-header">
              <p className="apt-obs-title">{t("observations")}</p>
              <button className="apt-obs-close" type="button" onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="apt-obs-body">
            {(selectedItem?.observations?.length > 0) && (
              <div className="ap-table__obs-history">
                {[...(selectedItem.observations)].map((obs, origIdx) => ({ obs, origIdx })).reverse().map(({ obs, origIdx }) => {
                  const currentUser = `${user?.firstname || ""} ${user?.lastname || ""}`.trim();
                  const isOwner = obs.userName === currentUser;
                  const isEditing = editingObsIdx === origIdx;
                  return (
                    <div key={origIdx} className="ap-table__obs-entry">
                      <span className="ap-table__obs-avatar" style={{ background: avatarColor(obs.userName) }}>
                        {getInitials(obs.userName)}
                      </span>
                      <div className="ap-table__obs-body">
                        <div className="ap-table__obs-meta">
                          <span className="ap-table__obs-user">{obs.userName}</span>
                          <span className="ap-table__obs-date">{obs.createdAt ? new Date(obs.createdAt).toLocaleDateString() : ""}</span>
                          {isOwner && !isEditing && (
                            <span className="ap-table__obs-actions">
                              <Icon name="pencil" link onClick={() => { setEditingObsIdx(origIdx); setEditingObsVal(obs.text); setEditingObsDate(obs.createdAt ? new Date(obs.createdAt).toISOString().split("T")[0] : ""); }} />
                              <Icon name="trash alternate" link color="red" onClick={() => handleDeleteObs(origIdx)} />
                            </span>
                          )}
                        </div>
                        {isEditing ? (
                          <div>
                            <textarea rows={2} value={editingObsVal} onChange={(e) => setEditingObsVal(e.target.value)}
                              style={{ width: "100%", marginBottom: "0.5rem", padding: "8px", borderRadius: "6px", border: "1px solid #D1D5DB", fontFamily: "'Archivo', sans-serif", resize: "none" }} />
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                              <button type="button" disabled={saving || !editingObsVal.trim()} onClick={handleSaveObsEdit} className="obs-icon-btn obs-icon-btn--confirm">
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 6.5L5 9.5L11 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </button>
                              <button type="button" onClick={() => { setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }} className="obs-icon-btn obs-icon-btn--cancel">
                                <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M8.5 2.5L2.5 8.5M2.5 2.5L8.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="ap-table__obs-text">{obs.text}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <textarea rows={3} value={obsVal} onChange={(e) => setObsVal(e.target.value)}
              placeholder={t("enter_observations")} />
            <div className="apt-obs-actions">
              <button type="button" disabled={saving || !obsVal.trim()} onClick={handleSaveObs} className="apt-obs-btn-confirm">
                {saving ? "…" : t("save")}
              </button>
              <button type="button" onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); }} className="apt-obs-btn-cancel">
                {t("cancel")}
              </button>
            </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Transferir a otro año */}
      {showTransfer && ReactDOM.createPortal(
        <div className="apt-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowTransfer(false); }}>
          <div className="apt-obs-modal">
            <div className="apt-obs-header">
              <p className="apt-obs-title">{t("ap_transfer_title")}</p>
              <button className="apt-obs-close" type="button" onClick={() => setShowTransfer(false)}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="apt-obs-body">
              <p className="apt-obs-transfer-text">
                {t("ap_transfer_confirm_prefix")} {selectedIds.size} {t("ap_transfer_confirm_middle")}{" "}
                <select
                  className="apt-obs-transfer-select"
                  value={transferYear}
                  onChange={(e) => setTransferYear(Number(e.target.value))}
                >
                  {[1, 2, 3].map((offset) => {
                    const y = (extYear || new Date().getFullYear()) + offset;
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </select>
                . {t("ap_transfer_confirm_suffix")}
              </p>
            </div>
            <div className="apt-obs-actions">
              <button type="button" disabled={transferring} onClick={handleTransfer} className="apt-obs-btn-confirm">
                {transferring ? "…" : t("ap_transfer_confirm_btn")}
              </button>
              <button type="button" onClick={() => setShowTransfer(false)} className="apt-obs-btn-cancel">
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Adjuntar archivos */}
      {showAttach && ReactDOM.createPortal(
        <div className="apt-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowAttach(false); }}>
          <div className="apt-obs-modal">
            <div className="apt-obs-header">
              <p className="apt-obs-title">{t("attach_files")}</p>
              <button className="apt-obs-close" onClick={() => setShowAttach(false)}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="apt-obs-body">
              <FileDropzone
                onUpload={handleUpload}
                uploading={uploading}
                onCancel={() => setShowAttach(false)}
                supportedLabel={t("ap_supported_files")}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Ver archivos */}
      {showFiles && ReactDOM.createPortal(
        <div className="apt-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) { setShowFiles(false); setPreviewUrl(null); } }}>
          <div className="apt-obs-modal">
            <div className="apt-obs-header">
              <p className="apt-obs-title">{t("attached_files")}</p>
              <button className="apt-obs-close" onClick={() => { setShowFiles(false); setPreviewUrl(null); }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            </div>
            <div className="apt-obs-body">
              {!selectedItem?.files?.length ? (
                <p style={{ color: "#999", textAlign: "center", padding: "1.5rem 0", margin: 0 }}>{t("no_attached_files")}</p>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px" }}>
                  {selectedItem.files.map((f, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "10px 12px", borderRadius: "10px", background: "#fff", border: "1px solid #E5E7EB", marginBottom: "8px" }}>
                      <Icon name={isImage(f.name) ? "image outline" : "file outline"} style={{ color: "#4A7FA5", margin: 0, fontSize: "1.1rem" }} />
                      <span style={{ flex: 1, fontSize: "0.875rem", color: "#374151", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</span>
                      <button title={t("view")} onClick={() => handleViewFile(f.key, f.name)}
                        style={{ border: "none", background: "#4A7FA5", color: "#fff", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="eye" style={{ margin: 0, fontSize: "0.8rem" }} />
                      </button>
                      <EvidenceTextButton module="annualPlan" fileKey={f.key} fileName={f.name} />
                      <button title={t("delete")} onClick={() => handleDeleteFile(f.key)} disabled={deletingFile !== null}
                        style={{ border: "none", background: deletingFile === f.key ? "#ccc" : "#E74C3C", color: "#fff", borderRadius: "50%", width: "30px", height: "30px", cursor: deletingFile !== null ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="trash" style={{ margin: 0, fontSize: "0.8rem" }} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {previewUrl && (
                <div style={{ marginTop: "0.5rem", marginBottom: "1rem", textAlign: "center" }}>
                  <p style={{ fontWeight: 600, fontSize: "0.875rem", color: "#374151", marginBottom: "0.5rem" }}>{previewName}</p>
                  <img src={previewUrl} alt={previewName} style={{ maxWidth: "100%", borderRadius: "8px" }} />
                  <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                    style={{ marginTop: "0.5rem", border: "none", background: "none", color: "#4A7FA5", cursor: "pointer", fontSize: "0.875rem", fontWeight: 600 }}>
                    {t("close")}
                  </button>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
                <button onClick={() => { setShowFiles(false); setPreviewUrl(null); }}
                  style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", background: "#9BAFC4", color: "#fff" }}>
                  {t("close")}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Escalación */}
      <BasicModal show={showEscal} close={() => setShowEscal(false)} size="small">
        {escalItem && (
          <div style={{ padding: "0.5rem 0" }}>
            <h3 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#1A2E4A", margin: "0 0 16px 0" }}>
              {t("ap_escalation_config")}
            </h3>
            <p style={{ fontSize: "0.85rem", color: "#555", marginBottom: "1.2rem" }}>
              {t("ap_escalation_desc")}
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.2rem" }}>
              {[
                { label: t("ap_resp_n1"),  key: "resp1",       type: "text",  ph: t("ap_full_name_ph") },
                { label: t("ap_email_n1"), key: "resp1_email", type: "email", ph: "email@empresa.com"  },
                { label: t("ap_resp_n2"),  key: "resp2",       type: "text",  ph: t("ap_full_name_ph") },
                { label: t("ap_email_n2"), key: "resp2_email", type: "email", ph: "email@empresa.com"  },
                { label: t("ap_resp_n3"),  key: "resp3",       type: "text",  ph: t("ap_full_name_ph") },
                { label: t("ap_email_n3"), key: "resp3_email", type: "email", ph: "email@empresa.com"  },
              ].map(({ label, key, type, ph }) => (
                <div key={key}>
                  <label style={{ display: "block", fontWeight: 600, marginBottom: "0.3rem", fontSize: "0.875rem", color: "#374151" }}>
                    {label}
                  </label>
                  <input
                    type={type}
                    value={escalForm[key] || ""}
                    onChange={(e) => setEscalForm((p) => ({ ...p, [key]: e.target.value }))}
                    placeholder={ph}
                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "0.875rem", outline: "none", boxSizing: "border-box", fontFamily: "'Archivo', sans-serif" }}
                  />
                </div>
              ))}
            </div>

            <label style={{ display: "block", fontWeight: 600, marginBottom: "0.5rem", fontSize: "0.875rem", color: "#374151" }}>
              {t("ap_escal_rules")}
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.75rem", marginBottom: "1.5rem" }}>
              {[
                { label: t("ap_level1_days"), key: "rule_l1", def: 0, min: 0 },
                { label: t("ap_level2_days"), key: "rule_l2", def: 3, min: 1 },
                { label: t("ap_level3_days"), key: "rule_l3", def: 7, min: 1 },
              ].map(({ label, key, def, min }) => (
                <div key={key}>
                  <label style={{ display: "block", fontSize: "0.8rem", color: "#6B7280", marginBottom: "0.3rem" }}>{label}</label>
                  <input
                    type="number" min={min} max="90"
                    value={escalForm[key] ?? def}
                    onChange={(e) => setEscalForm((p) => ({ ...p, [key]: Number(e.target.value) }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "0.875rem", outline: "none", textAlign: "center", boxSizing: "border-box", fontFamily: "'Archivo', sans-serif" }}
                  />
                </div>
              ))}
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: "14px" }}>
              <button
                type="button"
                disabled={savingEscal}
                onClick={handleSaveEscal}
                style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: savingEscal ? "not-allowed" : "pointer", background: "#4A7FA5", color: "#fff", opacity: savingEscal ? 0.55 : 1 }}
              >
                {savingEscal ? "…" : t("ap_save_escalation")}
              </button>
              <button
                type="button"
                onClick={() => setShowEscal(false)}
                style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", background: "#9BAFC4", color: "#fff" }}
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        )}
      </BasicModal>

      {/* Confirm Delete */}
      <ConfirmDialog
        open={showConfirm}
        title={t("delete")}
        message={t("confirm_delete_plan")}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleDelete}
        confirmLabel={t("accept")}
        cancelLabel={t("cancel")}
        confirmDanger
      />

      {/* Modal Nueva Área */}
      <AddCatalogPortal
        open={showAreaModal}
        title={t("new_area")}
        hasCode={true}
        t={t}
        onSave={async (vals) => { await areaController.createArea(accessToken, vals); await reloadAreas(true); }}
        onClose={() => setShowAreaModal(false)}
      />

      <AddCatalogPortal
        open={showActivityModal}
        title={t("new_activity")}
        hasCode={false}
        t={t}
        onSave={async (vals) => {
          if (!editingData.area) throw { msg: t("ap_select_area_first") };
          await activityController.createActivity(accessToken, { ...vals, area: editingData.area });
          await reloadActivities(true);
        }}
        onClose={() => setShowActivityModal(false)}
      />

      <AddCatalogPortal
        open={showFrequencyModal}
        title={t("new_frequency")}
        hasCode={true}
        t={t}
        onSave={async (vals) => { await frequencyController.createFrequency(accessToken, vals); await reloadFrequencies(true); }}
        onClose={() => setShowFrequencyModal(false)}
      />
    </div>
  );
}
